import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Tabs, 
  Tab, 
  CircularProgress, 
  Alert,
  Paper
} from '@mui/material';
import DataTable from '../components/DataTable';
import { 
  getAffinityContacts, 
  getAffinityCompanies, 
  getAffinityInteractions 
} from '../services/api';
import { 
  AffinityContact, 
  AffinityCompany, 
  AffinityInteraction 
} from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`affinity-tabpanel-${index}`}
      aria-labelledby={`affinity-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const AffinityData: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [contacts, setContacts] = useState<AffinityContact[]>([]);
  const [companies, setCompanies] = useState<AffinityCompany[]>([]);
  const [interactions, setInteractions] = useState<AffinityInteraction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch data based on the active tab
        if (tabValue === 0 && contacts.length === 0) {
          const contactsData = await getAffinityContacts();
          setContacts(contactsData);
        } else if (tabValue === 1 && companies.length === 0) {
          const companiesData = await getAffinityCompanies();
          setCompanies(companiesData);
        } else if (tabValue === 2 && interactions.length === 0) {
          const interactionsData = await getAffinityInteractions();
          setInteractions(interactionsData);
        }
      } catch (err) {
        setError('Failed to fetch data. Please try again later.');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tabValue, contacts.length, companies.length, interactions.length]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Define columns for each table
  const contactColumns = [
    { id: 'AffinityID', label: 'ID', width: '100px' },
    { id: 'Name', label: 'Name' },
    { id: 'Email', label: 'Email' },
    { id: 'Phone', label: 'Phone' },
    { id: 'Company', label: 'Company' },
    { id: 'LastInteractionDate', label: 'Last Interaction' }
  ];

  const companyColumns = [
    { id: 'CompanyID', label: 'ID', width: '100px' },
    { id: 'CompanyName', label: 'Company Name' },
    { id: 'Sector', label: 'Sector' },
    { id: 'EmployeeCount', label: 'Employees' },
    { id: 'HQRegion', label: 'Region' }
  ];

  const interactionColumns = [
    { id: 'InteractionID', label: 'ID', width: '100px' },
    { id: 'AffinityID', label: 'Contact ID' },
    { id: 'Type', label: 'Type' },
    { id: 'Date', label: 'Date' },
    { id: 'Outcome', label: 'Outcome' }
  ];

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Affinity CRM Data
      </Typography>
      
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Contacts" />
          <Tab label="Companies" />
          <Tab label="Interactions" />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TabPanel value={tabValue} index={0}>
        {loading && contacts.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable 
            title="Affinity Contacts" 
            data={contacts} 
            columns={contactColumns} 
          />
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {loading && companies.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable 
            title="Affinity Companies" 
            data={companies} 
            columns={companyColumns} 
          />
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {loading && interactions.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable 
            title="Affinity Interactions" 
            data={interactions} 
            columns={interactionColumns} 
          />
        )}
      </TabPanel>
    </div>
  );
};

export default AffinityData;